import React, { useState, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faReceipt,
  faMoneyBill,
  faFilter,
  faEye,
  faSpinner,
  faCheckCircle,
  faClose,
  faBalanceScale,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function ViewSalesRegister() {
  const { stateNavigator } = useNavigationEvent();

  const userId = ls.get("currentuserid");

  const [filterPaymentType, setFilterPaymentType] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const paymentOptions = [
    { value: "cash", label: "Cash", icon: faMoneyBill },
    { value: "credit_card", label: "Credit Card" },
    { value: "debit_card", label: "Debit Card" },
    { value: "mobile_money", label: "Mobile Money" },
    { value: "cheque", label: "Cheque" },
  ];

  // SWR Data fetching for Sales
  const { data: salessales, mutate: mutatesales } = useSWR(
    `${process.env.REACT_APP_SALES_ORDER_API}?include=field_cashier,field_customer&sort=-created&page[limit]=1000`,
    fetcher,
  );
  console.log("Sales Data:", salessales);

  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number,field_customer_address,drupal_internal__nid&page[limit]=1000`,
    fetcher,
  );
  console.log("Customers Data NOW:", customers);

  const { data: users } = useSWR(
    `${process.env.REACT_APP_USER_API}?&filter[drupal_internal__uid]=${userId}`,
    fetcher,
  );

  // Calculate statistics using useMemo for performance
  const statistics = useMemo(() => {
    if (!salessales?.data) {
      return {
        totalRevenue: 0,
        totalsales: 0,
        averagesale: 0,
        totalBalanceDue: 0,
        totalAmountPaid: 0,
      };
    }

    const sales = salessales.data;
    let totalRevenue = 0;
    let totalAmountPaid = 0;

    sales.forEach((sale) => {
      const amount = parseFloat(sale.attributes.field_total_amount || 0);
      const paid = parseFloat(sale.attributes.field_total_paid || 0);

      totalRevenue += amount;
      totalAmountPaid += paid;
    });

    const totalsales = sales.length;
    const averagesale = totalsales > 0 ? totalRevenue / totalsales : 0;
    const totalBalanceDue = totalRevenue - totalAmountPaid;

    return {
      totalRevenue,
      totalsales,
      averagesale,
      totalBalanceDue,
      totalAmountPaid,
    };
  }, [salessales]);

  const { totalRevenue, totalsales, totalBalanceDue } = statistics;

  // Filter sales based on payment type
  const filteredsales = useMemo(() => {
    if (!salessales?.data) return [];

    return salessales.data.filter((sale) => {
      if (filterPaymentType === "all") return true;
      return sale.attributes.field_payment_type === filterPaymentType;
    });
  }, [salessales, filterPaymentType]);

  // Pagination logic
  const totalPages = Math.ceil(filteredsales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSales = filteredsales.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterPaymentType]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table when changing pages
    document
      .querySelector(".table-container")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Helper function to calculate balance for a single sale
  const calculateSaleBalance = (sale) => {
    const total = parseFloat(sale.attributes.field_total_amount || 0);
    const paid = parseFloat(sale.attributes.field_total_paid || 0);
    return total - paid;
  };

  // Get balance display with formatting and styling
  const getBalanceDisplay = (sale) => {
    const balance = calculateSaleBalance(sale);

    if (balance > 0) {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#d32f2f",
        status: "Due",
      };
    } else if (balance < 0) {
      return {
        formatted: `GH₵${Math.abs(balance).toFixed(2)}`,
        color: "#ff9800",
        status: "Credit",
      };
    } else {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#388e3c",
        status: "Paid",
      };
    }
  };

  const handleDelete = async (salesId) => {
    try {
      const freshToken = await getCsrfToken();

      if (!freshToken) {
        setErrorMessage("Unable to authenticate. Please refresh the page.");
        setModalError(true);
        return;
      }

      if (window.confirm("Are you sure you want to delete this sale?")) {
        const res = await fetch(
          `${process.env.REACT_APP_SALES_ORDER_API}/${salesId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": freshToken,
            },
            credentials: "include",
          },
        );

        if (res.ok) {
          setModalSuccessMsg(true);
          mutatesales();
        } else {
          setErrorMessage("Failed to delete sale");
          setModalError(true);
        }
      }
    } catch (error) {
      setErrorMessage("Network error");
      setModalError(true);
    }
  };

  const getCashierName = (sale) => {
    const cashierId = sale.relationships?.field_cashier?.data?.id;
    if (!cashierId || !users?.data) return "N/A";
    const user = users.data.find((u) => u.id === cashierId);
    return user?.attributes?.name || "N/A";
  };

  const getCustomerName = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.title || "N/A";
  };

  const getCustomerPhoneNumber = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.field_phone_number || "N/A";
  };

  const handleFilter = () => {
    // Implement date range filtering
    mutatesales();
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Sales Register"
            buttonLabel="Add Sales"
            onClick={() => stateNavigator.navigate("salesorder")}
          />

          {/* FILTER BAR */}
          <div style={styles.filterBar}>
            <select
              style={styles.filterSelect}
              value={filterPaymentType}
              onChange={(e) => setFilterPaymentType(e.target.value)}
            >
              <option value="all">All Payment Types</option>
              {paymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              style={styles.filterInput}
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
            />
            <input
              type="date"
              style={styles.filterInput}
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
            />
            <button style={styles.filterButton} onClick={handleFilter}>
              <FontAwesomeIcon icon={faFilter} /> Filter
            </button>
          </div>

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faReceipt} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalsales}</h3>
                <p style={styles.statLabel}>Total Sales</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faMoneyBill} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{totalRevenue.toFixed(2)}</h3>
                <p style={styles.statLabel}>Total Sales Revenue</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faBalanceScale} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>
                  GH₵{totalBalanceDue.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Total Balance Due</p>
              </div>
            </div>
          </div>

          {/* SALE LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer} className="table-container">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Invoice Date</th>
                    <th>Customer Name</th>
                    <th>Customer Contact</th>
                    <th>Cashier</th>
                    <th>Total Amount</th>
                    <th>Amount Paid</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!salessales ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading Sales...
                      </td>
                    </tr>
                  ) : currentSales.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    currentSales.map((sale) => {
                      const balanceDisplay = getBalanceDisplay(sale);
                      return (
                        <tr key={sale.id}>
                          <td style={styles.td}>
                            {sale.attributes.field_order_number}
                          </td>
                          <td style={styles.td}>
                            {new Date(
                              sale.attributes.created,
                            ).toLocaleDateString()}
                          </td>
                          <td style={styles.td}>{getCustomerName(sale)}</td>
                          <td style={styles.td}>
                            {getCustomerPhoneNumber(sale)}
                          </td>
                          <td style={styles.td}>{getCashierName(sale)}</td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_amount,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_paid || 0,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                color: balanceDisplay.color,
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              {balanceDisplay.formatted}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <button
                              style={styles.viewButton}
                              onClick={() =>
                                stateNavigator.navigate(
                                  "viewsalesinvoicedetails",
                                  { salesId: sale.id },
                                )
                              }
                              title="View Invoice"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              style={styles.editButton}
                              onClick={() =>
                                stateNavigator.navigate("editsalesorder", {
                                  salesId: sale.id,
                                })
                              }
                              title="Edit Sale"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => handleDelete(sale.id)}
                              title="Delete Sale"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION CONTROLS */}
            {filteredsales.length > 0 && (
              <div style={styles.paginationContainer}>
                <div style={styles.itemsPerPageContainer}>
                  <label style={styles.itemsPerPageLabel}>Show:</label>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={styles.itemsPerPageSelect}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span style={styles.itemsPerPageText}>entries</span>
                </div>

                <div style={styles.paginationControls}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.paginationButton,
                      ...(currentPage === 1
                        ? styles.paginationButtonDisabled
                        : {}),
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </button>

                  <div style={styles.pageNumbers}>
                    {getPageNumbers().map((page, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          typeof page === "number" && handlePageChange(page)
                        }
                        disabled={page === "..."}
                        style={{
                          ...styles.pageNumberButton,
                          ...(currentPage === page ? styles.activePage : {}),
                          ...(page === "..." ? styles.pageNumberDots : {}),
                        }}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.paginationButton,
                      ...(currentPage === totalPages
                        ? styles.paginationButtonDisabled
                        : {}),
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                <div style={styles.paginationInfo}>
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredsales.length)} of{" "}
                  {filteredsales.length} entries
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="Sale deleted successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faFilter}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  th: {
    background: "#f8f9fa",
    padding: "14px",
    textAlign: "left",
    borderBottom: "1px solid #dee2e6",
    fontWeight: "600",
    color: "#333",
    fontSize: "14px",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid #dee2e6",
    fontSize: "14px",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  statCardHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
  },
  statIcon: {
    fontSize: "24px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  filterBar: {
    display: "flex",
    gap: "15px",
    marginBottom: "20px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterSelect: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    background: "white",
    minWidth: "200px",
    fontSize: "14px",
  },
  filterInput: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    minWidth: "150px",
    fontSize: "14px",
  },
  filterButton: {
    background: "#6c757d",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    transition: "background 0.2s",
  },
  filterButtonHover: {
    background: "#5a6268",
  },
  actionBTN: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginRight: "5px",
  },
  viewButtonHover: {
    backgroundColor: "#bbdefb",
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginRight: "5px",
  },
  deleteButtonHover: {
    backgroundColor: "#ffcdd2",
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    marginRight: "5px",
  },
  editButtonHover: {
    backgroundColor: "#c8e6c9",
  },
  // Pagination styles
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    padding: "15px 0",
    flexWrap: "wrap",
    gap: "15px",
  },
  itemsPerPageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  itemsPerPageLabel: {
    fontSize: "14px",
    color: "#666",
  },
  itemsPerPageSelect: {
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    cursor: "pointer",
  },
  itemsPerPageText: {
    fontSize: "14px",
    color: "#666",
  },
  paginationControls: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  paginationButton: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "background 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  paginationButtonDisabled: {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  },
  pageNumbers: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  pageNumberButton: {
    padding: "8px 12px",
    backgroundColor: "#f8f9fa",
    color: "#007bff",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    minWidth: "36px",
  },
  activePage: {
    backgroundColor: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  pageNumberDots: {
    cursor: "default",
    backgroundColor: "transparent",
    border: "none",
    color: "#666",
  },
  paginationInfo: {
    fontSize: "14px",
    color: "#666",
  },
};
